import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"

import { cn } from "@/lib/utils"

// The thumb travels between centres that sit half a thumb in from each end, so the
// tick dots are inset by the same amount to line up with the stops. Keep this in
// step with the thumb's h-*/w-* classes below.
const THUMB_SIZE_REM = 1.75
const TICK_INSET = `${THUMB_SIZE_REM / 2}rem`
const MAX_TICKS = 12

// Ticks only make sense for a slider with a handful of discrete stops; anything
// finer would render as a solid line of dots, so it is left plain.
const getTickCount = (min: number, max: number, step: number) => {
  const count = Math.round((max - min) / (step || 1)) + 1
  return Number.isFinite(count) && count >= 2 && count <= MAX_TICKS ? count : 0
}

// Track and thumb are a step chunkier than the shadcn defaults, and the thumb is
// filled rather than outlined, so the control still reads at menu scale.
const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    /** Marks each discrete stop with a dot on the track. */
    showTicks?: boolean
  }
>(({ className = '', "aria-label": ariaLabel, showTicks = false, min = 0, max = 100, step = 1, ...props }, ref) => {
  const tickCount = showTicks ? getTickCount(min, max, step) : 0

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex w-full touch-none select-none items-center",
        className
      )}
      min={min}
      max={max}
      step={step}
      {...props}
    >
      <SliderPrimitive.Track className={`relative ${className.includes("variant-small") ? "h-3" : "h-6"} w-full grow overflow-hidden rounded-full bg-primary/20`}>
        <SliderPrimitive.Range className="absolute h-full bg-primary" />
      </SliderPrimitive.Track>
      {/* Anchored to the root rather than the track: the thumb's travel is measured
          against the root box, so ticks placed here stay aligned under any padding. */}
      {tickCount ?
        <span className="pointer-events-none absolute inset-y-0" style={{ left: TICK_INSET, right: TICK_INSET }}>
          {Array.from({ length: tickCount }, (_, index) => (
            <span
              key={index}
              className="absolute top-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-black/25 dark:bg-white/40"
              style={{ left: `${(index / (tickCount - 1)) * 100}%` }}
            />
          ))}
        </span>
      : null}
      <SliderPrimitive.Thumb
        aria-label={ariaLabel}
        className={`block ${className.includes("variant-small") ? "h-4 w-4" : "h-7 w-7"} cursor-pointer rounded-full bg-black dark:bg-white shadow transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:pointer-events-none disabled:opacity-50`}
      />
    </SliderPrimitive.Root>
  )
})
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }
